import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Style from './PolicyViolations.module.css';
import GridComponent from '../common/grid/GridComponent';
import SearchBox from '../common/searchBox/searchBox';
import SolidUp from '../../assets/Arrow_up_solid.svg';
import SolidDown from '../../assets/Arrow_down_solid.svg';
import {
  getPolicyViolations,
  getPolicyViolationsCountByType,
  getPolicyViolationsCountByCategory,
  setPolicyViolationsFilters,
} from '../../store/entities/policyViolations';
// The extension name map lives in the governance slice, not this screen's: it is the same
// Configuration-DB reference data Extension Controls reads, just reduced to id -> name.
import { listExtensionNames } from '../../store/entities/governance';

// Sub-tabs mirror the wireframe (claude-dashboards/dashboards/incident-feed.html,
// "Policy Violations" tab). Extension Violations is out of scope for this delivery —
// Extension Violations is now a live sub-tab. It differs from the others in one way
// worth stating on screen: evaluation is match-all, so one extension can appear more
// than once for the same device -- once per rule it matched, including the default
// "Any other extension" rule. Governance folds those into a single status; this feed
// deliberately does not.
const SUB_TABS = [
  { key: 'DATA', label: 'Data Violations', enabled: true },
  { key: 'WEB,AI', label: 'Web and AI Violations', enabled: true },
  { key: 'EXTENSION', label: 'Extension Violations', enabled: true },
];

// Matches the backend's dateRange enum (governanceViolationRead.js RELATIVE_DATE_FILTERS).
const DATE_RANGE_OPTIONS = [
  { value: 'last30d', label: 'Last 30 days' },
  { value: 'last60d', label: 'Last 60 days' },
  { value: 'last90d', label: 'Last 90 days' },
  { value: 'last3m', label: 'Last 3 months' },
  { value: 'last6m', label: 'Last 6 months' },
  { value: 'last9m', label: 'Last 9 months' },
  { value: 'last1y', label: 'Last 1 year' },
  { value: 'last2y', label: 'Last 2 years' },
  { value: 'last3y', label: 'Last 3 years' },
  { value: 'custom', label: 'Custom range' },
];

const ACTION_BADGE = {
  BLOCK: { label: 'Block User', cls: Style.critical },
  WARN: { label: 'Warn User', cls: Style.moderate },
  REPORT: { label: 'Report', cls: Style.low },
  IGNORE: { label: 'Ignore', cls: Style.neutral },
};
const actionLabel = (action) => (ACTION_BADGE[action] || { label: action || '—' }).label;

const PolicyViolations = (props) => {
  const {
    records, totalCount, listLoading,
    countByType, filters, selectedCustomer, extensionNames,
  } = props;
  const isExtensionTab = filters.controlType === 'EXTENSION';
  const customerId = props.match?.params?.id;

  const [sortField, setSortField] = useState(filters.sortBy);
  const [sortOrder, setSortOrder] = useState(filters.sortDir);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Aggregate calls (stat tiles) are driven off the customer + date window only —
  // the list itself is fetched by GridComponent's own resetOnChange mechanism below.
  useEffect(() => {
    if (!customerId) return;
    props.getPolicyViolationsCountByType(customerId, filters);
    props.getPolicyViolationsCountByCategory(customerId, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, filters.dateRange, filters.fromDate, filters.toDate]);

  // The id -> name map for the Extension Violations tab. Fetched only when that tab is active,
  // and not re-fetched as the user pages or changes the date window: it is a small, slow-moving
  // reference set, not part of the violation query.
  useEffect(() => {
    if (!customerId || !isExtensionTab) return;
    props.listExtensionNames(customerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, isExtensionTab]);

  const countFor = (ctrlType) => (countByType.find((c) => c.ctrlType === ctrlType) || {}).count || 0;
  const dataCount = countFor('DATA');
  const webAiCount = countFor('WEB') + countFor('AI');
  const extensionCount = countFor('EXTENSION');

  const activeTabKey = filters.controlType;

  const handleTabClick = (tab) => {
    if (!tab.enabled || tab.key === activeTabKey) return;
    props.setPolicyViolationsFilters({ controlType: tab.key, page: 1 });
  };

  const handleSearch = (searchText) => {
    props.setPolicyViolationsFilters({ search: searchText, page: 1 });
  };

  const handleSort = (field) => {
    const nextOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(nextOrder);
    props.setPolicyViolationsFilters({ sortBy: field, sortDir: nextOrder, page: 1 });
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      // Wait for both custom dates to be picked (Apply button) before refetching.
      props.setPolicyViolationsFilters({ dateRange: 'custom' });
    } else {
      props.setPolicyViolationsFilters({ dateRange: value, fromDate: null, toDate: null, page: 1 });
    }
  };

  const applyCustomRange = () => {
    if (!customFrom || !customTo) return;
    props.setPolicyViolationsFilters({ dateRange: 'custom', fromDate: customFrom, toDate: customTo, page: 1 });
  };

  const renderSortIcon = (field) => (
    <img
      style={{ width: '13px', marginLeft: '5px' }}
      src={sortField === field && sortOrder === 'desc' ? SolidDown : SolidUp}
      alt="Sort"
    />
  );

  const sortableHeader = (field, label) => (
    <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort(field)}>
      {label}{renderSortIcon(field)}
    </div>
  );

  // The Extension Violations tab reports on an EXTENSION, not a page: there is no URL to put
  // under Destination, and Category is a sensitive-data concept that never applies. So that tab
  // swaps in the extension's name and drops Category; every other tab is untouched.
  //
  // Not sortable, unlike Destination: the name is resolved CLIENT-SIDE from the lookup (see
  // extensionNames), so the server cannot order by it and a sort control would silently only
  // reorder the current page.
  const gridDef = {
    name: 'policyViolationsGrid',
    columns: [
      { fieldName: 'detectedOn', displayName: sortableHeader('detectedOn', 'Detected On'), dataType: 'date', subType: 'datetime', width: '18%' },
      { fieldName: 'user', displayName: sortableHeader('user', 'User'), width: '14%', className: Style.mono },
      { fieldName: 'action', displayName: 'Event', width: '14%' },
      isExtensionTab
        ? { fieldName: 'extensionName', displayName: 'Extension', width: '44%', maxWidth: '350px' }
        : { fieldName: 'destination', displayName: sortableHeader('url', 'Destination'), width: '26%', maxWidth: '350px', className: `${Style.mono} ${Style.urlCell}` },
      ...(isExtensionTab
        ? []
        : [{ fieldName: 'category', displayName: sortableHeader('category', 'Category'), width: '18%' }]),
    ],
  };

  const gridRecords = records.map((r) => ({
    ...r,
    user: r.user || '—',
    action: actionLabel(r.action),
    destination: r.url || r.domain || r.app || '—',
    // r.app is the AppIdentifier -- the LOGICAL extension id. Falls back to it when the name is
    // unknown (a lookup older than the extension, or past the 200-row map) so the row stays
    // identifiable rather than going blank.
    extensionName: extensionNames[r.app] || r.app || '—',
    category: r.category || '—',
  }));

  const getNextPageData = (pageNo, pageSize) => {
    props.getPolicyViolations(customerId, { ...filters, page: pageNo, pageSize });
  };
  const getPrevPageData = (pageNo, pageSize) => {
    props.getPolicyViolations(customerId, { ...filters, page: pageNo, pageSize });
  };

  // Any change to the customer or a filter that should reset to page 1 flows through
  // this single key — GridComponent's own componentDidUpdate re-fetches page 1
  // whenever resetOnChange changes (see LicenseManagementTab/BrandPolicies precedent).
  //
  // selectedCustomer.id is part of the key for a reason: GridComponent only fetches on
  // mount when `selectedCustomer.id === params` (GridComponent.jsx componentDidMount),
  // and on a hard refresh `customers.selected` is still null at that point, so that
  // mount fetch is skipped. The route param never changes, so without the redux id in
  // this key nothing would ever retry and the list would stay empty while the count
  // APIs (driven by the useEffect above) still populate. Same fix as
  // LicenseManagementTab, which passes selectedCustomer.id as its whole reset key.
  const resetOnChange = [
    customerId, selectedCustomer?.id, filters.search, filters.sortBy, filters.sortDir,
    filters.controlType, filters.dateRange, filters.fromDate, filters.toDate,
  ].join('|');

  return (
    <div className={Style.page}>
      <h1 className={Style.pageTitle}>Policy Violations</h1>
      <p className={Style.pageSubtitle}>
        Data, Web, AI, and browser-extension control policy violations reported by the extension.
      </p>

      <div className={Style.statRow3}>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Data Violations</div>
          <div className={Style.statValue}>{dataCount}</div>
        </div>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Web and AI Violations</div>
          <div className={Style.statValue}>{webAiCount}</div>
        </div>
        <div className={Style.statCard}>
          <div className={Style.statLabel}>Extension Violations</div>
          <div className={Style.statValue}>{extensionCount}</div>
        </div>
      </div>

      <div className={Style.subtabBar}>
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${Style.subtabBtn} ${tab.key === activeTabKey ? Style.subtabBtnActive : ''} ${!tab.enabled ? Style.subtabBtnDisabled : ''}`}
            onClick={() => handleTabClick(tab)}
            disabled={!tab.enabled}
            title={tab.enabled ? '' : 'Not enabled in this scope'}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* {activeTabKey === 'EXTENSION' && (
        <div className={Style.disabledPanel}>
          <div className={Style.disabledPanelInner}>
            <div className={Style.disabledPanelText}>
              Every rule an extension matches is reported separately, so one extension can appear
              more than once for the same device — once per rule, including the default
              &ldquo;Any other extension&rdquo; rule. Governance shows a single folded status per
              extension; this feed is deliberately unfolded.
            </div>
          </div>
        </div>
      )} */}

      <>
          <div className={Style.filterBar}>
            <SearchBox
              width="280px"
              placeholder="Search by user, action, URL, domain, or category..."
              value={filters.search}
              handleSearch={handleSearch}
            />
            <select className={Style.dateSelect} value={filters.dateRange} onChange={handleDateRangeChange}>
              {DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {filters.dateRange === 'custom' && (
              <div className={Style.customRangeRow}>
                <input type="date" className={Style.dateInput} value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
                <span>to</span>
                <input type="date" className={Style.dateInput} value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
                <button className={Style.dateInput} onClick={applyCustomRange}>Apply</button>
              </div>
            )}
          </div>

          <div className={Style.card}>
            <GridComponent
              loaderFlag={listLoading}
              resetOnChange={resetOnChange}
              data={gridRecords}
              totalRecords={totalCount}
              pageSize={filters.pageSize}
              definition={gridDef}
              getPrevPageData={getPrevPageData}
              getNextPageData={getNextPageData}
              rowClick={() => {}}
              params={customerId}
            />
          </div>
      </>
    </div>
  );
};

const mapStateToProps = (state) => ({
  records: state.entities.policyViolations.records,
  totalCount: state.entities.policyViolations.totalCount,
  listLoading: state.entities.policyViolations.listLoading,
  countByType: state.entities.policyViolations.countByType,
  // Optional-chained: this component is mounted by tests (and could be by any host) with a
  // store that carries only the policyViolations slice. The names are a display nicety --
  // absent them every row falls back to the logical id -- so a missing slice must not throw.
  extensionNames: state.entities.governance?.extensionNames || {},
  filters: state.entities.policyViolations.filters,
  selectedCustomer: state.entities.customers.selected,
});

const mapDispatchToProps = (dispatch) => ({
  getPolicyViolations: (customerId, filters) => dispatch(getPolicyViolations(customerId, filters)),
  getPolicyViolationsCountByType: (customerId, filters) => dispatch(getPolicyViolationsCountByType(customerId, filters)),
  getPolicyViolationsCountByCategory: (customerId, filters) => dispatch(getPolicyViolationsCountByCategory(customerId, filters)),
  listExtensionNames: (customerId) => dispatch(listExtensionNames(customerId)),
  setPolicyViolationsFilters: (partial) => dispatch(setPolicyViolationsFilters(partial)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PolicyViolations));
